import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, FileText, Package, Users, Receipt, BarChart3,
  Settings, LogOut, ChevronDown, Plus, Crown, ShieldCheck, User as UserIcon,
  Building2, BookOpen, ScrollText, Scale, Wrench, Repeat, Menu, X,
  ClipboardList, ShoppingCart, Boxes, Landmark, FileMinus,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCompany } from "@/lib/company";
import { SerpLogo } from "@/components/SiteHeader";

const NAV = [
  { key: "dashboard", to: "/app/dashboard", label: "Dashboard",  icon: LayoutDashboard, testid: "nav-dashboard" },
  { key: "quotations", to: "/app/quotations",  label: "Quotations", icon: ClipboardList, testid: "nav-quotations" },
  { key: "sales_orders", to: "/app/sales-orders", label: "Sales Orders", icon: ShoppingCart, testid: "nav-sales-orders" },
  { key: "invoices",  to: "/app/invoices",  label: "Invoices",   icon: FileText,        testid: "nav-invoices" },
  { key: "credit_notes", to: "/app/credit-notes", label: "Credit Notes", icon: FileMinus, testid: "nav-credit-notes" },
  { key: "purchases", to: "/app/purchases", label: "Purchases",  icon: Receipt,         testid: "nav-purchases" },
  { key: "purchase_orders", to: "/app/purchase-orders", label: "Purchase Orders", icon: ClipboardList, testid: "nav-purchase-orders" },
  { key: "job_cards", to: "/app/job-cards", label: "Job Cards",  icon: Wrench,          testid: "nav-job-cards" },
  { key: "vouchers",  to: "/app/vouchers",  label: "Vouchers",   icon: ScrollText,      testid: "nav-vouchers" },
  { key: "recurring_vouchers", to: "/app/recurring-vouchers", label: "Recurring", icon: Repeat, testid: "nav-recurring" },
  { key: "ledgers",   to: "/app/ledgers",   label: "Ledgers",    icon: BookOpen,        testid: "nav-ledgers" },
  { key: "books",     to: "/app/books",     label: "Books",      icon: Scale,           testid: "nav-books" },
  { key: "items",     to: "/app/items",     label: "Items",      icon: Package,         testid: "nav-items" },
  { key: "stock",     to: "/app/stock-summary", label: "Stock Summary", icon: Boxes,   testid: "nav-stock" },
  { key: "fixed_assets", to: "/app/fixed-assets", label: "Fixed Assets", icon: Landmark, testid: "nav-fixed-assets" },
  { key: "parties",   to: "/app/parties",   label: "Parties",    icon: Users,           testid: "nav-parties" },
  { key: "expenses",  to: "/app/expenses",  label: "Expenses",   icon: Receipt,         testid: "nav-expenses" },
  { key: "reports",   to: "/app/reports",   label: "GST Reports", icon: BarChart3,       testid: "nav-reports" },
  { key: "settings",  to: "/app/settings",  label: "Settings",   icon: Settings,        testid: "nav-settings" },
];

function RoleChip({ role }) {
  if (role === "owner") return <span className="chip chip-info"><Crown size={11}/>Owner</span>;
  if (role === "admin") return <span className="chip chip-info"><ShieldCheck size={11}/>Admin</span>;
  if (role === "accountant") return <span className="chip chip-neutral">Accountant</span>;
  if (role === "sales") return <span className="chip chip-neutral">Sales</span>;
  return <span className="chip chip-neutral"><UserIcon size={11}/>Readonly</span>;
}

function CompanySwitcher({ compact = false }) {
  const { companies, active, setActive } = useCompany();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (!active) {
    return (
      <Link to="/app/setup" data-testid="company-setup-cta" className="btn-primary text-xs">
        <Plus size={14}/> Create Company
      </Link>
    );
  }
  return (
    <div className="relative">
      <button
        data-testid="company-switcher-btn"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={`flex items-center gap-2 px-3 border border-[#E2E8F0] hover:border-[#FF5500] rounded-lg text-[#0F172A] bg-white transition-colors ${
          compact ? "py-2 min-w-[160px] max-w-[220px]" : "py-1.5 min-w-[220px] max-w-[280px]"
        }`}
        style={{ minHeight: 44 }}
      >
        <Building2 size={14} className="text-[#FF5500] shrink-0" />
        <div className="flex-1 text-left truncate">
          <div className="font-semibold truncate text-sm">{active.name}</div>
          <div className="text-[10px] text-[#64748B] font-mono-data truncate">{active.country} · {active.base_currency}</div>
        </div>
        <ChevronDown size={14} className="text-[#64748B] shrink-0" />
      </button>
      {open && (
        <div
          data-testid="company-switcher-menu"
          className="absolute top-full mt-1 left-0 w-[280px] sm:w-[320px] bg-white border border-[#E2E8F0] rounded-lg shadow-xl z-50 py-1 max-h-[400px] overflow-y-auto"
        >
          {companies.map((c) => (
            <button
              key={c.id}
              data-testid={`switch-company-${c.id}`}
              onMouseDown={(e) => {
                e.preventDefault();
                setActive(c.id);
                setOpen(false);
                navigate("/app/dashboard");
              }}
              className={`w-full text-left px-3 py-2.5 hover:bg-[#F1F5F9] flex items-center gap-2 ${c.id === active.id ? "bg-[#FFEDE0]" : ""}`}
            >
              <Building2 size={14} className="text-[#FF5500] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0F172A] truncate">{c.name}</div>
                <div className="text-[11px] text-[#64748B] font-mono-data">{c.country} · {c.base_currency} · {c.tax_regime}</div>
              </div>
              <RoleChip role={c.my_role} />
            </button>
          ))}
          <div className="border-t border-[#E2E8F0] mt-1 pt-1">
            <button
              data-testid="create-new-company-btn"
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); navigate("/app/setup"); }}
              className="w-full text-left px-3 py-2.5 hover:bg-[#F1F5F9] flex items-center gap-2 text-[#FF5500] font-medium text-sm"
            >
              <Plus size={14}/> Create New Company
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Sidebar body — reused across mobile drawer, tablet rail, desktop full. */
function SidebarBody({ user, isSuperAdmin, visibleNav, onNavigate, onLogout, variant }) {
  // variant: 'full' | 'rail' | 'drawer'
  const showLabels = variant !== "rail";
  return (
    <>
      <Link
        to="/app/dashboard"
        onClick={onNavigate}
        className={`mx-3 mt-3 flex items-center justify-center rounded-xl ${showLabels ? "px-4 py-3" : "px-2 py-2"}`}
        style={{
          background: "rgba(255, 255, 255, 0.97)",
          minHeight: showLabels ? 56 : 44,
          boxShadow: "0 1px 2px rgba(0,0,0,0.12) inset, 0 6px 16px rgba(0,0,0,0.22)",
        }}
        data-testid={`sidebar-brand-${variant}`}
      >
        {showLabels ? (
          <SerpLogo height={30} testid={`sidebar-logo-${variant}`} />
        ) : (
          <img
            src="/serp-mark.png"
            alt="SERP"
            data-testid={`sidebar-logo-${variant}`}
            style={{ height: 28, width: 28, objectFit: "contain" }}
            draggable={false}
          />
        )}
      </Link>

      <nav className="py-3 flex-1 overflow-y-auto">
        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={n.testid}
            onClick={onNavigate}
            title={showLabels ? undefined : n.label}
            className={({ isActive }) =>
              `sidebar-item ${isActive ? "active" : ""} ${showLabels ? "" : "sidebar-item-rail"}`
            }
          >
            <n.icon size={18} strokeWidth={1.8} />
            {showLabels && <span>{n.label}</span>}
          </NavLink>
        ))}
      </nav>

      <div className={`${showLabels ? "p-4" : "p-2"}`} style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        {showLabels ? (
          <>
            <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-1">Signed in</div>
            <div className="text-white text-sm font-semibold truncate" data-testid="user-name-sidebar">
              {user?.name || user?.email}
            </div>
            {isSuperAdmin && (
              <NavLink
                to="/app/admin"
                data-testid="nav-admin-console"
                onClick={onNavigate}
                className={({ isActive }) => `mt-2 flex items-center gap-2 text-xs font-semibold transition-colors ${isActive ? "text-[#FF7A33]" : "text-[#FCD34D] hover:text-[#FF7A33]"}`}
              >
                <Crown size={12}/> Admin Console
              </NavLink>
            )}
            <button
              data-testid="logout-btn"
              onClick={onLogout}
              className="mt-3 w-full text-left text-[#94A3B8] hover:text-[#FF7A33] flex items-center gap-2 text-xs uppercase tracking-widest transition-colors"
              style={{ minHeight: 40 }}
            >
              <LogOut size={14} /> Sign out
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            {isSuperAdmin && (
              <NavLink
                to="/app/admin"
                onClick={onNavigate}
                title="Admin Console"
                data-testid="nav-admin-console-rail"
                className={({ isActive }) => `p-2 rounded-md ${isActive ? "text-[#FF7A33] bg-white/5" : "text-[#FCD34D] hover:text-[#FF7A33]"}`}
              >
                <Crown size={16} />
              </NavLink>
            )}
            <button
              onClick={onLogout}
              title="Sign out"
              data-testid="logout-btn-rail"
              className="p-2 rounded-md text-[#94A3B8] hover:text-[#FF7A33] hover:bg-white/5"
            >
              <LogOut size={16} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout, isSuperAdmin } = useAuth();
  const { active } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const menu = active?.menu_settings || {};
  const visibleNav = NAV.filter((n) => {
    if (isSuperAdmin) return true;
    if (n.key === "dashboard") return true;
    return menu[n.key] !== false;
  });

  return (
    <div className="min-h-screen flex bg-[#F7F8FB]">
      {/* Desktop sidebar (full) — >= lg */}
      <aside
        className="hidden lg:flex w-60 min-h-screen flex-col sticky top-0 h-screen navy-rail"
        style={{ background: "#0F172A", borderRight: "1px solid #1E293B" }}
        data-testid="sidebar-desktop"
      >
        <SidebarBody
          user={user} isSuperAdmin={isSuperAdmin} visibleNav={visibleNav}
          onLogout={onLogout} variant="full"
        />
      </aside>

      {/* Tablet sidebar (icon rail with hover-expand) — md..lg */}
      <aside
        className="hidden md:flex lg:hidden sidebar-rail group min-h-screen flex-col sticky top-0 h-screen navy-rail"
        style={{ background: "#0F172A", borderRight: "1px solid #1E293B" }}
        data-testid="sidebar-tablet"
      >
        <div className="sidebar-rail-collapsed flex flex-col h-full">
          <SidebarBody
            user={user} isSuperAdmin={isSuperAdmin} visibleNav={visibleNav}
            onLogout={onLogout} variant="rail"
          />
        </div>
        <div className="sidebar-rail-expanded flex flex-col h-full">
          <SidebarBody
            user={user} isSuperAdmin={isSuperAdmin} visibleNav={visibleNav}
            onLogout={onLogout} variant="full"
          />
        </div>
      </aside>

      {/* Mobile drawer — < md */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 md:hidden"
          data-testid="mobile-drawer-overlay"
          role="dialog"
          aria-modal="true"
        >
          <button
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            data-testid="mobile-drawer-scrim"
          />
          <aside
            className="relative w-[280px] max-w-[85vw] h-full flex flex-col navy-rail animate-slide-in"
            style={{ background: "#0F172A", borderRight: "1px solid #1E293B" }}
            data-testid="sidebar-mobile"
          >
            <button
              onClick={() => setDrawerOpen(false)}
              className="absolute top-3 right-3 z-10 p-2 rounded-md text-[#94A3B8] hover:text-white hover:bg-white/10 flex items-center justify-center"
              aria-label="Close menu"
              data-testid="drawer-close-btn"
              style={{ minWidth: 44, minHeight: 44 }}
            >
              <X size={20} />
            </button>
            <SidebarBody
              user={user} isSuperAdmin={isSuperAdmin} visibleNav={visibleNav}
              onNavigate={() => setDrawerOpen(false)}
              onLogout={onLogout} variant="drawer"
            />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0" style={{ background: "#F7F8FB" }}>
        <header
          className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 flex items-center justify-between sticky top-0 z-30 bg-white border-b gap-2"
          style={{ borderColor: "#E2E8F0", minHeight: 60 }}
          data-testid="workspace-header"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Hamburger — mobile only */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="flex md:hidden items-center justify-center p-2 rounded-md text-[#0F172A] hover:bg-[#F1F5F9] shrink-0"
              aria-label="Open menu"
              data-testid="hamburger-menu-btn"
              style={{ minWidth: 44, minHeight: 44 }}
            >
              <Menu size={22} />
            </button>
            <div className="min-w-0 flex-1 sm:flex-none">
              <CompanySwitcher compact />
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {active && (
              <Link
                to="/app/invoices/new"
                data-testid="quick-new-invoice"
                className="btn-primary"
                style={{ minHeight: 44 }}
              >
                <Plus size={14}/> <span className="hidden sm:inline">New Invoice</span><span className="sm:hidden">New</span>
              </Link>
            )}
          </div>
        </header>
        <main className="flex-1 p-3 sm:p-4 lg:p-6 max-w-[1600px] w-full mx-auto">
          {!active && (
            <div className="card-surface p-6 sm:p-8 text-center" data-testid="no-active-company">
              <Building2 size={48} className="mx-auto text-[#94A3B8] mb-3"/>
              <h2 className="text-lg font-semibold text-[#0F172A]">No active company</h2>
              <p className="text-sm text-[#64748B] mt-2 mb-4">Set up your first company to start invoicing.</p>
              <Link to="/app/setup" data-testid="setup-first-company" className="btn-primary">
                <Plus size={14}/> Create Company
              </Link>
            </div>
          )}
          {active && <Outlet />}
        </main>
      </div>
    </div>
  );
}
